'use client';

import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import type { ComponentPropsWithoutRef } from 'react';

function isRouteActive(pathname: string, href: string, end?: boolean): boolean {
  if (end) {
    return pathname === href;
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

type NavLinkProps = Omit<
  ComponentPropsWithoutRef<typeof NextLink>,
  'className' | 'href'
> & {
  href: string;
  className: string | ((args: { isActive: boolean }) => string);
  end?: boolean;
};

/**
 * Active-state navigation link (replaces react-router `NavLink`).
 */
export function NavLink({ href, className, end, ...props }: NavLinkProps) {
  const pathname = usePathname();
  const active = isRouteActive(pathname, href, end);
  const resolvedClass =
    typeof className === 'function'
      ? className({ isActive: active })
      : className;

  return (
    <NextLink
      href={href}
      className={resolvedClass}
      aria-current={active ? 'page' : undefined}
      {...props}
    />
  );
}
